import type { StateCreator } from 'zustand';
import type { ViewType, Notification } from '../../types';
import type { PresenceUser } from '../../../shared/types/socket';

// ============================================================================
// UI SLICE - Manages UI state (views, sidebar, tools, notifications)
// ============================================================================

export interface UISlice {
  // View state
  activeView: ViewType;
  setActiveView: (view: ViewType) => void;

  // Active tab state
  activeTab: string;
  setActiveTab: (tab: string) => void;

  // Sidebar state
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;

  // Tool selection
  selectedTool: 'select' | 'node' | 'edge' | 'pan' | 'delete';
  setSelectedTool: (tool: 'select' | 'node' | 'edge' | 'pan' | 'delete') => void;

  // Element selection
  selectedElements: string[];
  selectElement: (id: string, multi?: boolean) => void;
  deselectElement: (id: string) => void;
  clearSelection: () => void;
  isSelected: (id: string) => boolean;

  // Zoom and Pan
  zoomLevel: number;
  panOffset: { x: number; y: number };
  setZoom: (level: number) => void;
  setPan: (offset: { x: number; y: number }) => void;
  resetView: () => void;

  // Dialogs state
  saveDialogOpen: boolean;
  loadDialogOpen: boolean;
  exportDialogOpen: boolean;
  workspaceDialogOpen: boolean;
  newProjectDialogOpen: boolean;
  notificationsOpen: boolean;
  setSaveDialogOpen: (open: boolean) => void;
  setLoadDialogOpen: (open: boolean) => void;
  setExportDialogOpen: (open: boolean) => void;
  setWorkspaceDialogOpen: (open: boolean) => void;
  setNewProjectDialogOpen: (open: boolean) => void;
  setNotificationsOpen: (open: boolean) => void;

  // Notifications
  notifications: Notification[];
  addNotification: (type: Notification['type'], message: string) => void;
  removeNotification: (id: string) => void;
  markNotificationAsRead: (id: string) => void;
  clearNotifications: () => void;
  getUnreadCount: () => number;

  // Search
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  clearSearch: () => void;

  // Export progress
  exportInProgress: boolean;
  exportLabel: string | null;
  exportProgress: number | null;
  startExport: (label: string, progress?: number | null) => void;
  updateExportProgress: (progress: number | null) => void;
  finishExport: () => void;

  // Real-time presence (Socket.IO P0-2)
  presenceUsers: PresenceUser[];
  setPresenceUsers: (users: PresenceUser[]) => void;
}

export const createUISlice: StateCreator<UISlice> = (set, get, _api) => ({
  // Initial state
  activeView: 'pert',
  activeTab: 'pert',
  sidebarCollapsed: false,
  selectedTool: 'select',
  selectedElements: [],
  zoomLevel: 1,
  panOffset: { x: 0, y: 0 },
  notifications: [],
  searchQuery: '',
  saveDialogOpen: false,
  loadDialogOpen: false,
  exportDialogOpen: false,
  workspaceDialogOpen: false,
  newProjectDialogOpen: false,
  notificationsOpen: false,
  exportInProgress: false,
  exportLabel: null,
  exportProgress: null,
  presenceUsers: [],

  // View actions
  setActiveView: (view) => set({ activeView: view }),

  // Active tab actions
  setActiveTab: (tab) => set({ activeTab: tab }),

  // Sidebar actions
  toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
  setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),

  // Tool actions
  setSelectedTool: (tool) => set({ selectedTool: tool }),

  // Dialog actions
  setSaveDialogOpen: (open) => set({ saveDialogOpen: open }),
  setLoadDialogOpen: (open) => set({ loadDialogOpen: open }),
  setExportDialogOpen: (open) => set({ exportDialogOpen: open }),
  setWorkspaceDialogOpen: (open) => set({ workspaceDialogOpen: open }),
  setNewProjectDialogOpen: (open) => set({ newProjectDialogOpen: open }),
  setNotificationsOpen: (open) => set({ notificationsOpen: open }),

  // Selection actions
  selectElement: (id, multi = false) =>
    set((state) => ({
      selectedElements: multi
        ? state.selectedElements.includes(id)
          ? state.selectedElements
          : [...state.selectedElements, id]
        : [id],
    })),

  deselectElement: (id) =>
    set((state) => ({
      selectedElements: state.selectedElements.filter((elId) => elId !== id),
    })),

  clearSelection: () => set({ selectedElements: [] }),

  isSelected: (id) => get().selectedElements.includes(id),

  // Zoom and Pan actions
  setZoom: (level) => set({ zoomLevel: Math.max(0.1, Math.min(3, level)) }),

  setPan: (offset) => set({ panOffset: offset }),

  resetView: () => set({ zoomLevel: 1, panOffset: { x: 0, y: 0 } }),

  // Notification actions
  addNotification: (type, message) =>
    set((state) => ({
      notifications: [
        {
          id: `notif-${Date.now()}-${Math.random()}`,
          type,
          message,
          timestamp: new Date(),
          read: false,
        },
        ...state.notifications,
      ],
    })),

  removeNotification: (id) =>
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
    })),

  markNotificationAsRead: (id) =>
    set((state) => ({
      notifications: state.notifications.map((n) =>
        n.id === id ? { ...n, read: true } : n
      ),
    })),

  clearNotifications: () => set({ notifications: [] }),

  getUnreadCount: () => get().notifications.filter((n) => !n.read).length,

  // Search actions
  setSearchQuery: (query) => set({ searchQuery: query }),
  clearSearch: () => set({ searchQuery: '' }),

  // Export progress actions
  startExport: (label, progress = null) =>
    set({
      exportInProgress: true,
      exportLabel: label,
      exportProgress: progress,
    }),
  updateExportProgress: (progress) => set({ exportProgress: progress }),
  finishExport: () =>
    set({
      exportInProgress: false,
      exportLabel: null,
      exportProgress: null,
    }),

  // Presence actions
  setPresenceUsers: (users) => set({ presenceUsers: users }),
});
